import * as React from 'react'
import styled from 'styled-components'
import I18n from '../../../../core/i18n'
import { IJsonResponse } from '../../../../core/JsonApiSerializer'
import { agentService, utilService } from '../../../../core/services'
import * as constants from '../../../../static/constants'
import * as interfaces from '../../../../static/interfaces'
import * as Hooks from '../../../../utils/hooks'
import { formatDatetimeJST } from '../../../../utils/dateFormat'
import { Button, Panel, Select } from '../../../atoms'
import { EditTable } from '../../../organisms'
import SidebarLayout from '../../_layouts/sidebarLayout'

declare var window: interfaces.IWindow

interface IProps {
  terms_versions: {
    terms_versions: IJsonResponse
  }
  user: IJsonResponse
}

// 表示用ステータスラベル
const STATUS_LABELS: { [key: string]: string } = {
  draft: '下書き',
  scheduled: '公開予定',
  in_effect: '施行中',
  deleted: '削除済み',
}

// ソート選択肢 (value: `<column>_<direction>`)
const SORT_OPTIONS = [
  { label: '施行日時 降順', value: 'effective_at_desc' },
  { label: '施行日時 昇順', value: 'effective_at_asc' },
  { label: '作成日時 降順', value: 'created_at_desc' },
  { label: '作成日時 昇順', value: 'created_at_asc' },
]

const DEFAULT_SORT = 'effective_at_desc'

const getCurrentSortValue = (): string => {
  const params = new URLSearchParams(window.location.search)
  const sort = params.get('sort')
  const direction = params.get('direction')
  if (sort && direction) {
    const value = `${sort}_${direction}`
    if (SORT_OPTIONS.some(o => o.value === value)) {
      return value
    }
  }

  return DEFAULT_SORT
}

const COLUMNS = [
  {
    name: I18n.t('generic.id'),
    field: 'id',
    required: false,
    readOnly: true,
    className: 'small-col-left',
  },
  {
    name: '名前',
    field: 'name',
    required: true,
    renderElement: record => (
      <VersionCell>
        <span>{record.name}</span>
        {record.is_latest && <LatestBadge>最新版</LatestBadge>}
      </VersionCell>
    ),
  },
  {
    name: 'メッセージ',
    field: 'message',
    required: false,
    renderElement: record => <div>{record.message || '-'}</div>,
  },
  {
    name: '状態',
    field: 'display_status',
    required: false,
    className: 'small-col',
    renderElement: record => (
      <div>{STATUS_LABELS[record.display_status] || record.display_status}</div>
    ),
  },
  {
    name: '施行日時',
    field: 'effective_at',
    required: false,
    renderElement: record => <div>{formatDatetimeJST(record.effective_at)}</div>,
  },
  {
    name: '同意済み',
    field: 'agreed_count',
    required: false,
    className: 'small-col',
    renderElement: record => <div style={{ textAlign: 'right' }}>{record.agreed_count}</div>,
  },
  {
    name: '同意率',
    field: 'agreement_rate',
    required: false,
    className: 'small-col',
    renderElement: record => <div style={{ textAlign: 'right' }}>{record.agreement_rate}%</div>,
  },
  {
    name: '作成日時',
    field: 'created_at',
    required: false,
    renderElement: record => <div>{formatDatetimeJST(record.created_at)}</div>,
  },
]

const AgentTermsVersionsIndex: React.FC<IProps> = props => {
  Hooks.SetDocumentTitle('ガイド利用規約 一覧')

  const { data: userData } = utilService.getDataFromJson(props.user)
  const { data: termsVersionsList, pagination } = agentService.getDataFromJson(
    props.terms_versions.terms_versions
  )

  const [termsVersions, setTermsVersions] = React.useState(termsVersionsList)

  const handleDelete = React.useCallback(
    async id => {
      const { flush } = await agentService.deleteTermsVersion(id)
      window.flashMessages.addMessage({
        text: flush.message,
        type: flush.type,
      })
      if (flush.type === 'success') {
        setTermsVersions(termsVersions.filter(t => t.id !== id))
      }
    },
    [termsVersions]
  )

  const handleClickDelete = id => {
    if (window.confirm(I18n.t('generic.delete_confirmation'))) {
      handleDelete(id)
    }
  }

  // ソート変更: `<column>_<direction>` 形式を sort/direction クエリに分解して再読込
  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    const match = value.match(/^(.+)_(asc|desc)$/)
    if (!match) {
      return
    }
    const [, column, direction] = match
    const params = new URLSearchParams(window.location.search)
    params.set('sort', column)
    params.set('direction', direction)
    location.href = `${location.pathname}?${params.toString()}`
  }

  return (
    <SidebarLayout user={userData}>
      <Wrapper>
        <Panel
          title={
            <Heading>
              <span>ガイド利用規約 一覧</span>
              <Button primary={true}>
                <a href="/agent/terms_versions/new">{I18n.t('generic.create')}</a>
              </Button>
            </Heading>
          }
        >
          <SortBar>
            <Select
              name="sort_order"
              options={SORT_OPTIONS}
              defaultValue={getCurrentSortValue()}
              onChangeHandler={handleSortChange}
            />
          </SortBar>
          <EditTable
            editable={true}
            columns={COLUMNS}
            records={termsVersions}
            pagination={pagination}
            handleDelete={handleClickDelete}
            getEditLink={({ id }) => `/agent/terms_versions/${id}/edit`}
            getRowLink={record => `/agent/terms_versions/${record.id}`}
          />
        </Panel>
      </Wrapper>
    </SidebarLayout>
  )
}

const Wrapper = styled.div`
  width: 100%;
  padding: 22px 40px 80px 46px;

  .Panel {
    max-width: none;
  }

  .Table .Table_Header,
  .Table .Table_Data {
    padding-left: 8px;
    padding-right: 8px;
  }

  .Table .small-col-left {
    min-width: 40px;
    width: 40px;
  }

  .Table .small-col {
    min-width: 80px;
    width: 80px;
    text-align: center;
  }
`

const Heading = styled.div`
  .Button {
    margin-left: 12px;
  }
`

// テーブル上部のソート選択UI (トラブル報告と同様の形式)
const SortBar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
`

const VersionCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

// 施行中の最新規約を強調表示するバッジ (水色)
const LatestBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  background: ${constants.COLORS.info};
  color: #fff;
  border-radius: 10px;
  font-size: 11px;
  font-weight: bold;
  line-height: 1.2;
  letter-spacing: 0.5px;
`

export default AgentTermsVersionsIndex
